import React, { useEffect, useState } from 'react';
import { States } from '@/helpers/common/states';
import api, { AxiosError } from '@/services/api';
import { EventMachineSaleProductReport } from '@/model/EventMachineSaleProductReport';
import { toast } from 'react-toastify';
import { ReportControllerProps } from '../../types';
import { SaleProductMachineContent } from './ui';

interface StateProps {
  reportController: ReportControllerProps;
  setState: (state: States) => void;
  onExportPDF?: (machines: EventMachineSaleProductReport[]) => void;
}

export const SaleProductMachine: React.FC<StateProps> = ({
  reportController,
  setState,
  onExportPDF,
}): JSX.Element => {
  const { eventId, singleEvent } = reportController;
  const [machines, setMachines] = useState<EventMachineSaleProductReport[]>([]);

  const [serialNumber, setSerialNumber] = useState<string>('');
  const [productName, setProductName] = useState<string>('');

  const handleGetSaleProducts = async (
    filterSerial = serialNumber,
    filterProduct = productName,
  ): Promise<void> => {
    try {
      setState(States.loading);

      const params = new URLSearchParams();
      params.append('singleEvent', String(singleEvent));
      if (filterSerial.trim()) {
        params.append('serialNumber', filterSerial.trim());
      }
      if (filterProduct.trim()) {
        params.append('productName', filterProduct.trim());
      }

      const { data } = await api.get<EventMachineSaleProductReport[]>(
        `event/report/${eventId}/sale-product/machine?${params.toString()}`,
      );
      if (data) {
        setMachines(data);
      }
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleClearFilter = (): void => {
    setSerialNumber('');
    setProductName('');
    handleGetSaleProducts('', '');
  };

  useEffect(() => {
    handleGetSaleProducts();
  }, [singleEvent]);

  return (
    <SaleProductMachineContent
      machines={machines}
      serialNumber={serialNumber}
      productName={productName}
      onChangeSerialNumber={setSerialNumber}
      onChangeProductName={setProductName}
      onFilter={() => handleGetSaleProducts()}
      onClearFilter={handleClearFilter}
      onExportPDF={onExportPDF ? () => onExportPDF(machines) : undefined}
    />
  );
};
